import { FC, ReactNode, useCallback, useMemo } from 'react';
import { Controller, FormProvider, FormState, useForm } from 'react-hook-form';
import { Grid, Stack, TextField, Typography } from '@mui/material';
import { Emergency } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { BoxWell } from '@shared/components/BoxWell';
import { DateInputFormat } from '@shared/helpers/date';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { StoryTagAutocomplete } from '@modules/Stories/components/StoryTagAutocomplete';
import { MeetingStatusSelect } from '@modules/Meetings/components/MeetingStatusSelect';


export type FormCrud = Meetings.Create | Meetings.Edit;

interface Props {
    defaultValues?: Partial<FormCrud>;
    onSubmitRequest: (data: FormCrud) => void;
    slots?: {
      footer?: ReactNode | ((formState: FormState<FormCrud>) => ReactNode);
      storiesThrough?: ReactNode | ((formState: FormState<FormCrud>) => ReactNode);
    },
}

const DEFAULT_VALUES: Partial<FormCrud> = {
  title: '',
  audience: '',
  desiredOutcome: '',
  status: 'preparing',
  date: null,
  tags: [],
};

export const MeetingForm: FC<Props> = (props) => {
  const { defaultValues, onSubmitRequest, slots } = props;

  const dictionary = useDictionary();

  const mergedDefaultValues = useMemo(() => {
    const initialValues = {
      ...DEFAULT_VALUES,
      ...defaultValues,
    };
    return {
      ...initialValues,
      title: initialValues.title || '',
      tags: initialValues.tags || [],
      audience: initialValues.audience || '',
      desiredOutcome: initialValues.desiredOutcome || '',
      date: initialValues.date || null,
    };
  }, [defaultValues]);

  const methods = useForm<FormCrud>({
    defaultValues: mergedDefaultValues,
  });

  const { handleSubmit, formState } = methods;

  const onSubmit = useCallback(async (data: FormCrud) => {
    await onSubmitRequest(data);
  }, [onSubmitRequest]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <BoxWell sx={{ height: '100%', p: { xs: 2, sm: 5 } }}>
              <Grid container spacing={2}>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack direction="row">
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      {dictionary.forms.meeting.fieldTitle}
                    </Typography>
                    <Emergency color="error" sx={{ fontSize: 8, mt: 0.5 }}/>
                  </Stack>
                  <Controller
                    name="title"
                    rules={{ required: dictionary.forms.validations.required }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        multiline
                        minRows={1}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        placeholder={dictionary.forms.meeting.fieldTitlePlaceholder}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack direction="row">
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      {dictionary.forms.meeting.fieldDate}
                    </Typography>
                    <Emergency color="error" sx={{ fontSize: 8, mt: 0.5 }}/>
                  </Stack>
                  <Controller
                    name="date"
                    rules={{ required: dictionary.forms.validations.required }}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        openTo="year"
                        views={['year', 'month', 'day']}
                        format={DateInputFormat}
                        onChange={(date) => {
                          field.onChange(date);
                        }}
                        slotProps={{
                          textField: {
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Stack direction="row">
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      {dictionary.forms.meeting.fieldAudience}
                    </Typography>
                    <Emergency color="error" sx={{ fontSize: 8, mt: 0.5 }}/>
                  </Stack>
                  <Controller
                    name="audience"
                    rules={{ required: dictionary.forms.validations.required }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        multiline
                        minRows={1}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        placeholder={dictionary.forms.meeting.fieldAudiencePlaceholder}
                      />
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Stack direction="row">
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      {dictionary.forms.meeting.fieldDesiredOutcome}
                    </Typography>
                    <Emergency color="error" sx={{ fontSize: 8, mt: 0.5 }}/>
                  </Stack>
                  <Controller
                    name="desiredOutcome"
                    rules={{ required: dictionary.forms.validations.required }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        multiline
                        minRows={1}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        placeholder={dictionary.forms.meeting.fieldDesiredOutcomePlaceholder}
                      />
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Stack direction="row">
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      {dictionary.forms.meeting.fieldStatus}
                    </Typography>
                    <Emergency color="error" sx={{ fontSize: 8, mt: 0.5 }}/>
                  </Stack>
                  <Controller
                    name="status"
                    render={({ field, fieldState }) => (
                      <MeetingStatusSelect
                        {...field}
                        value={field.value || ''}
                        fullWidth
                        label=""
                        onChange={(event) => {
                          const selectedValue = event.target.value;
                          field.onChange(selectedValue);
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Typography variant="body2" fontWeight={500} mb={1}>
                    {dictionary.forms.meeting.fieldTags}
                  </Typography>
                  <Controller
                    name="tags"
                    render={({ field, fieldState }) => (
                      <StoryTagAutocomplete
                        {...field}
                        freeSolo
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>

              </Grid>
            </BoxWell>
          </Grid>

          <Grid size={{ xs: 12 }}>
            {slots?.storiesThrough ? (
              typeof slots.storiesThrough === 'function' ? slots.storiesThrough(formState) : slots.storiesThrough
            ) : null}
          </Grid>

          <Grid size={{ xs: 12 }}>
            {slots?.footer ? (
              typeof slots.footer === 'function' ? slots.footer(formState) : slots.footer
            ) : null}
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
